import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    IconButton,
    InputAdornment,
    ListItem,
    ListItemIcon,
    ListItemText,
    Slider,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import {
    AddCircleOutlineOutlined,
    RemoveCircleOutlineOutlined,
    Search,
} from "@mui/icons-material";
import {
    INCLUDE_EXCLUDE_BORDER,
    INCLUDE_EXCLUDE_HEIGHT_OF_HEADER,
} from "../IncludeExcludeFilter";
import React, {useEffect, useRef, useState, useCallback, useMemo} from "react";
import {useVirtualizer} from "@tanstack/react-virtual";
import {
    DatePicker,
    DateRangePicker,
    LocalizationProvider,
    SingleInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import {AdapterLuxon} from "@mui/x-date-pickers-pro/AdapterLuxon";
import {debounce} from "lodash";
import {
    FilterAction,
    FilterOption,
    FilterState,
    IncludeExcludeFilterCategory,
} from "../reducers/includeExcludeFilterReducer";
import ClearIcon from "@mui/icons-material/Clear";
import {useTextTruncation} from "../../../utils/useTextTruncation";
import {formatNumberRangeBound} from "../../../utils/filterNumberRangeUtils";
import {getDisplayLabel} from "./treeViewFilterListSelectionUtils";

const MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH = 2;



// Define a type for flattened options
interface FlattenedOption extends FilterOption {
    key: string;
    level: number;
}

interface SelectAllOption {
    key: string;
    level: number;
    item: string;
    isSelectAll: true;
    checked: boolean;
    indeterminate: boolean;
    label?: string;
}

type RowOption = FlattenedOption | SelectAllOption;

interface NumberRangeRowProps {
    style: React.CSSProperties;
    option: FlattenedOption;
    selectedCategory: string;
    getType: () => "include" | "exclude";
    dispatch: React.Dispatch<FilterAction>;
    valueFormat?: "currency" | "number";
}

const NumberRangeRow: React.FC<NumberRangeRowProps> = ({
    style,
    option,
    selectedCategory,
    getType,
    dispatch,
    valueFormat,
}) => {
    const min = option.rangeMin ?? 0;
    const max = option.rangeMax ?? 100;
    const step = option.rangeStep ?? 1;
    const committedMin = option.numberRange?.[0];
    const committedMax = option.numberRange?.[1];

    const [value, setValue] = useState<[number, number]>([committedMin ?? min, committedMax ?? max]);
    // Mirrors the inputs as raw text so partial entries like "-" or "" stay editable while typing.
    const [draft, setDraft] = useState<[string, string]>([
        String(committedMin ?? min),
        String(committedMax ?? max),
    ]);

    const commit = useCallback(
        (next: [number, number]) => {
            setValue(next);
            setDraft([String(next[0]), String(next[1])]);
            dispatch({
                type: "SET_NUMBER_RANGE",
                payload: {
                    filterType: getType(),
                    parentCategoryId: selectedCategory,
                    categoryId: option.categoryId,
                    numberRange: next,
                },
            });
        },
        [dispatch, getType, selectedCategory, option.categoryId],
    );

    // Clamps to the slider bounds and keeps the edited end from crossing the other.
    const commitBound = useCallback(
        (edge: 0 | 1) => {
            const parsed = Number(draft[edge]);

            if (draft[edge].trim() === "" || !Number.isFinite(parsed)) {
                setDraft([String(value[0]), String(value[1])]);
                return;
            }

            const clamped = Math.min(Math.max(parsed, min), max);
            const next: [number, number] =
                edge === 0
                    ? [Math.min(clamped, value[1]), value[1]]
                    : [value[0], Math.max(clamped, value[0])];

            commit(next);
        },
        [draft, value, min, max, commit],
    );

    // Re-sync when the committed range changes elsewhere, such as clearing a chip.
    useEffect(() => {
        const nextMin = committedMin ?? min;
        const nextMax = committedMax ?? max;
        setValue([nextMin, nextMax]);
        setDraft([String(nextMin), String(nextMax)]);
    }, [committedMin, committedMax, min, max]);

    const boundInputProps = (edge: 0 | 1) => ({
        type: "number" as const,
        size: "small" as const,
        label: edge === 0 ? "From" : "To",
        value: draft[edge],
        onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
            setDraft((prev) => {
                const next: [string, string] = [prev[0], prev[1]];
                next[edge] = event.target.value;
                return next;
            }),
        onBlur: () => commitBound(edge),
        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
                event.preventDefault();
                commitBound(edge);
            }
        },
        slotProps: {
            htmlInput: {min, max, step, "aria-label": edge === 0 ? "From" : "To"},
            input: valueFormat === "currency"
                ? {startAdornment: <InputAdornment position="start">$</InputAdornment>}
                : undefined,
        },
        sx: {flex: 1},
    });

    return (
        <Box sx={style}>
            <Box sx={{mt: 2, mx: 3, width: "auto"}}>
                <Typography variant="caption" color="text.secondary">
                    {option.label?.trim() || option.item?.trim() || "Range"}
                </Typography>
                <Slider
                    value={value}
                    min={min}
                    max={max}
                    step={step}
                    disableSwap
                    valueLabelDisplay="auto"
                    valueLabelFormat={(sliderValue: number) =>
                        formatNumberRangeBound(sliderValue, valueFormat)
                    }
                    onChange={(_, next) => {
                        const [nextMin, nextMax] = next as [number, number];
                        setValue([nextMin, nextMax]);
                        setDraft([String(nextMin), String(nextMax)]);
                    }}
                    onChangeCommitted={(_, next) => commit(next as [number, number])}
                />
                <Box sx={{display: "flex", justifyContent: "space-between", mt: 0.5, mb: 1.5}}>
                    <Typography variant="caption" color="text.secondary">
                        {formatNumberRangeBound(min, valueFormat)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {formatNumberRangeBound(max, valueFormat)}
                    </Typography>
                </Box>
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <TextField {...boundInputProps(0)} />
                    <TextField {...boundInputProps(1)} />
                </Box>
            </Box>
        </Box>
    );
};

interface RowProps {
    index: number;
    style: React.CSSProperties;
    flattenedOptionsWithSelectAll: RowOption[];
    selectedCategoryData: IncludeExcludeFilterCategory | undefined;
    getType: () => "include" | "exclude";
    selectedCategory: string;
    dispatch: React.Dispatch<FilterAction>;
}

const Row: React.FC<RowProps> = ({
    index,
    style,
    flattenedOptionsWithSelectAll,
    selectedCategoryData,
    getType,
    selectedCategory,
    dispatch,
}) => {
    const opt = flattenedOptionsWithSelectAll[index];
    if (!opt) return null;

    const displayLabel =
        "isSelectAll" in opt && opt.isSelectAll ? opt.item : getDisplayLabel(opt);
    const {ref, isTruncated} = useTextTruncation([displayLabel]);

    if ("isSelectAll" in opt && opt.isSelectAll) {
        return (
            <ListItem
                key={opt.key}
                onClick={() =>
                    dispatch({
                        type: "SELECT_ALL",
                        payload: {filterType: getType(), categoryId: selectedCategory},
                    })
                }
                sx={{pl: 2}}
                style={style}
            >
                <ListItemIcon sx={{minWidth: "auto"}}>
                    <Checkbox
                        edge="start"
                        checked={opt.checked}
                        indeterminate={opt.indeterminate}
                        color="secondary"
                        tabIndex={-1}
                        disableRipple
                    />
                </ListItemIcon>
                <ListItemText
                    primary={opt.label?.trim() || opt.item}
                    slotProps={{
                        primary: {style: {fontSize: "14px"}},
                    }}
                    sx={{my: 0, cursor: "pointer"}}
                />
            </ListItem>
        );
    }

    // Date-range row
    if (selectedCategoryData?.type === "date-range") {
        return (
            <Box sx={style}>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <Box sx={{mt: 2, mx: 2, width: "auto"}}>
                        <DateRangePicker
                            sx={{width: "100%"}}
                            slots={{field: SingleInputDateRangeField}}
                            slotProps={{
                                field: {
                                    // @ts-expect-error MUI date range field accepts InputLabelProps at runtime
                                    InputLabelProps: {shrink: true},
                                },
                            }}
                            key={opt.key}
                            label={opt.label?.trim() || opt.item?.trim() || "Date Range"}
                            value={
                                "dateRange" in opt &&
                                Array.isArray(opt.dateRange) &&
                                opt.dateRange.length === 2
                                    ? opt.dateRange
                                    : undefined
                            }
                            onChange={(newValue) =>
                                dispatch({
                                    type: "SET_DATE_RANGE",
                                    payload: {
                                        filterType: getType(),
                                        parentCategoryId: selectedCategory,
                                        categoryId: "categoryId" in opt ? opt.categoryId : "",
                                        dateRange: newValue,
                                    },
                                })
                            }
                        />
                    </Box>
                </LocalizationProvider>
            </Box>
        );
    }

    if (selectedCategoryData?.type === "date-operator") {
        return (
            <Box sx={style}>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <Box sx={{mt: 2, mx: 2, width: "auto"}}>
                        <DatePicker
                            sx={{width: "100%"}}
                            key={opt.key}
                            label={opt.label?.trim() || opt.item?.trim() || ""}
                            value={"dateValue" in opt ? opt.dateValue ?? null : null}
                            format="M/d/yyyy"
                            slotProps={{
                                actionBar: {
                                    actions: ["clear"],
                                },
                                textField: {
                                    fullWidth: true,
                                    size: "small",
                                    InputLabelProps: {shrink: true},
                                },
                            }}
                            onChange={(newValue) =>
                                dispatch({
                                    type: "SET_DATE_OPERATOR",
                                    payload: {
                                        filterType: getType(),
                                        categoryId: selectedCategory,
                                        operator: "dateOperator" in opt && opt.dateOperator
                                            ? opt.dateOperator
                                            : (opt.item.toLowerCase() as "after" | "before"),
                                        dateValue: newValue,
                                    },
                                })
                            }
                        />
                    </Box>
                </LocalizationProvider>
            </Box>
        );
    }

    if (selectedCategoryData?.type === "number-range") {
        return (
            <NumberRangeRow
                key={opt.key}
                style={style}
                option={opt as FlattenedOption}
                selectedCategory={selectedCategory}
                getType={getType}
                dispatch={dispatch}
                valueFormat={selectedCategoryData.valueFormat}
            />
        );
    }

    // Checkbox row
    return (
        <ListItem
            key={opt.key}
            onClick={() =>
                dispatch({
                    type: "TOGGLE_OPTION",
                    payload: {
                        filterType: getType(),
                        topLevelCategoryId: selectedCategory,
                        categoryId: "categoryId" in opt ? opt.categoryId : selectedCategory,
                        optionId: opt.item,
                    },
                })
            }
            disablePadding
            sx={{pl: 3 + opt.level * 2, py: 0, alignItems: "center"}}
            style={style}
        >
            <ListItemIcon sx={{minWidth: "auto"}}>
                <Checkbox
                    edge="start"
                    checked={opt.checked}
                    tabIndex={-1}
                    disableRipple
                    color="secondary"
                />
            </ListItemIcon>
            <Tooltip
                title={isTruncated ? displayLabel : ""}
                followCursor
            >
                <ListItemText
                    ref={ref}
                    primary={displayLabel}
                    slotProps={{
                        primary: {
                            style: {
                                fontSize: "14px",
                                lineHeight: 1.2,
                            },
                        },
                    }}
                    sx={{
                        my: 0,
                        pr: 2,
                        cursor: "pointer",
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                />
            </Tooltip>
        </ListItem>
    );
};


export const FilterListSelection = ({
                                        validCategories,
                                        selectedCategory,
                                        filteredOptions,
                                        activeTab,
                                        setActiveTab,
                                        filterSelectionState,
                                        dispatch,
                                        handleServerSearch,
                                        resetSearchSignal,
                                        serverSearchLoading,
                                        serverSearchMinChars = MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH,
                                        onModeSwitch,
                                    }: {
    validCategories: IncludeExcludeFilterCategory[];
    selectedCategory: string;
    filteredOptions: {
        include: IncludeExcludeFilterCategory[];
        exclude: IncludeExcludeFilterCategory[];
    };
    activeTab: number;
    setActiveTab: (tabIndex: number) => void;
    filterSelectionState: FilterState;
    dispatch: React.Dispatch<FilterAction>;
    handleServerSearch?: (categoryId: string, search: string) => void;
    resetSearchSignal?: number;
    serverSearchLoading?: boolean;
    serverSearchMinChars?: number;
    onModeSwitch?: (filterType: "include" | "exclude") => void;
}) => {
    const theme = useTheme();
    const listScrollRef = useRef<HTMLDivElement>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [serverError, setServerError] = useState<string | null>(null);

    const debouncedSetSearch = useMemo(
        () => debounce((value: string) => {
            setDebouncedSearchTerm(value);
        }, 250),
        []
    );

    useEffect(() => {
        return () => {
            debouncedSetSearch.cancel();
        };
    }, [debouncedSetSearch]);

    useEffect(() => {
        if (resetSearchSignal !== undefined) {
            setSearchTerm("");
            setDebouncedSearchTerm("");
            debouncedSetSearch.cancel();
            setServerError(null);
        }
    }, [resetSearchSignal, debouncedSetSearch]);

    const getType = () => (activeTab === 0 ? "include" : "exclude");

    const selectedCategoryData = filterSelectionState.options[getType()].find(
        (cat) => cat.id === selectedCategory,
    );

    const flattenOptions = (
        options: FilterOption[] | undefined,
        level: number = 0,
        parentKey: string = "",
    ): FlattenedOption[] => {
        if (!options) return [];

        const flattened: FlattenedOption[] = [];
        for (let index = 0; index < options.length; index++) {
            const opt = options[index];
            const uniqueKey = `${parentKey}-${opt.categoryId || "unknown"}-${opt.item}-${level}-${index}`;
            flattened.push({...opt, level, key: uniqueKey});

            if (opt.subItems?.length) {
                flattened.push(...flattenOptions(opt.subItems, level + 1, uniqueKey));
            }
        }
        return flattened;
    };

    const hierarchicalSearch = useCallback(
        (options: FilterOption[], termToSearch: string): FilterOption[] => {
            if (!termToSearch || termToSearch.length < MINIMUM_CHAR_LENGTH_FOR_SERVER_SEARCH) {
                return options;
            }

            const lowerSearch = termToSearch.toLowerCase();

            const searchRecursive = (
                opt: FilterOption
            ): { matched: boolean; filteredOpt: FilterOption | null } => {
                const itemMatches =
                    opt.item.toLowerCase().includes(lowerSearch) ||
                    opt.label?.toLowerCase().includes(lowerSearch);

                const matchedSubItems: FilterOption[] = [];
                let hasMatchingDescendants = false;

                if (opt.subItems?.length) {
                    for (const subItem of opt.subItems) {
                        const {matched, filteredOpt} = searchRecursive(subItem);
                        if (matched && filteredOpt) {
                            matchedSubItems.push(filteredOpt);
                            hasMatchingDescendants = true;
                        }
                    }
                }

                const shouldInclude = itemMatches || hasMatchingDescendants;

                if (shouldInclude) {
                    return {
                        matched: true,
                        filteredOpt: {
                            ...opt,
                            subItems: matchedSubItems.length > 0 ? matchedSubItems : opt.subItems,
                        },
                    };
                }

                return {matched: false, filteredOpt: null};
            };

            const results: FilterOption[] = [];
            for (const opt of options) {
                const {matched, filteredOpt} = searchRecursive(opt);
                if (matched && filteredOpt) {
                    results.push(filteredOpt);
                }
            }

            return results;
        },
        []
    );

    const optionsList = filteredOptions[activeTab === 0 ? "include" : "exclude"];

    const categoryOptions = React.useMemo(() => {
        const categoryData = optionsList.find((cat) => cat.id === selectedCategory);
        if (!categoryData) return [];

        const searchResults = hierarchicalSearch(
            categoryData.options,
            debouncedSearchTerm
        );

        if (categoryData.preserveOptionOrder) return searchResults;

        return searchResults.sort((a, b) =>
            a.item.localeCompare(b.item, undefined, {sensitivity: "base"})
        );
    }, [optionsList, debouncedSearchTerm, selectedCategory, hierarchicalSearch]);

    const flattenedOptions = React.useMemo(
        () => flattenOptions(categoryOptions),
        [categoryOptions],
    );

    const flattenedOptionsWithSelectAll: RowOption[] = React.useMemo(() => {
        if (!selectedCategoryData || selectedCategoryData.type === "date-range" || selectedCategoryData.type === "date-operator" || selectedCategoryData.type === "number-range") {
            return flattenedOptions;
        }

        const selectAllOption: SelectAllOption = {
            key: "select-all",
            level: 0,
            item: "Select All...",
            isSelectAll: true,
            checked:
                selectedCategoryData.options.length > 0 &&
                selectedCategoryData.options.every((opt) => opt.checked),
            indeterminate:
                selectedCategoryData.options.some((opt) => opt.checked) &&
                !selectedCategoryData.options.every((opt) => opt.checked),
        };

        return [selectAllOption, ...flattenedOptions];
    }, [flattenedOptions, selectedCategoryData]);

    const isDateType = selectedCategoryData?.type === "date-range" || selectedCategoryData?.type === "date-operator";
    const isNumberRangeType = selectedCategoryData?.type === "number-range";
    const itemSize = isNumberRangeType ? 140 : isDateType ? 92 : 42;
    const hasSelectAll = !!selectedCategoryData && !isDateType && !isNumberRangeType;
    const selectAllSize = 58;

    const virtualizer = useVirtualizer({
        count: flattenedOptionsWithSelectAll.length,
        getScrollElement: () => listScrollRef.current,
        estimateSize: (index) => (index === 0 && hasSelectAll) ? selectAllSize : itemSize,
        overscan: 10,
    });

    const virtualItems = virtualizer.getVirtualItems();
    const totalSize = virtualizer.getTotalSize();

    useEffect(() => {
        if (listScrollRef.current) {
            listScrollRef.current.scrollTop = 0;
        }
        virtualizer.measure();
    }, [virtualizer, itemSize, flattenedOptionsWithSelectAll.length, selectedCategory]);

    const selectedCategoryObj = validCategories.find(
        (cat) => cat.id === selectedCategory,
    );
    const isServerSide = !!selectedCategoryObj?.serverSideSearch;
    const isIncludeOnly = !!selectedCategoryObj?.includeOnly;

    // Force include tab when category is include-only
    useEffect(() => {
        if (isIncludeOnly && activeTab !== 0) {
            setActiveTab(0);
        }
    }, [isIncludeOnly, activeTab, setActiveTab]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSetSearch(value);
        setServerError(null);
        if (isServerSide && handleServerSearch) {
            if (value.length >= serverSearchMinChars) {
                handleServerSearch(selectedCategory, value);
            } else {
                dispatch({
                    type: "CLEAR_SERVER_OPTIONS",
                    payload: {filterType: getType(), categoryId: selectedCategory},
                });
            }
        }
    };

    const handleClearSearch = () => {
        setSearchTerm("");
        setDebouncedSearchTerm("");
        debouncedSetSearch.cancel();
        setServerError(null);
        if (isServerSide && handleServerSearch) {
            dispatch({
                type: "CLEAR_SERVER_OPTIONS",
                payload: {filterType: getType(), categoryId: selectedCategory},
            });
        }
    };

    return (
        <Box
            flex={1}
            borderRight={INCLUDE_EXCLUDE_BORDER}
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minHeight={0}
        >
            <Box
                display="flex"
                alignItems="center"
                sx={{
                    borderBottom: INCLUDE_EXCLUDE_BORDER,
                    height: INCLUDE_EXCLUDE_HEIGHT_OF_HEADER,
                }}
            >
                {selectedCategoryObj?.type === "checkbox" ? (
                    <TextField
                        fullWidth
                        placeholder={`Search ${selectedCategoryObj?.label || ""}...`}
                        variant="standard"
                        size="small"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        slotProps={{
                            input: {
                                startAdornment: <Search color="action" sx={{mr: 1}}/>,
                                endAdornment: searchTerm ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={handleClearSearch}
                                            aria-label="Clear search"
                                        >
                                            <ClearIcon fontSize="small"/>
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                                disableUnderline: true,
                            },
                        }}
                        sx={{m: 1}}
                    />
                ) : (
                    <Box sx={{m: 1, width: "100%", height: 40}}/>
                )}
            </Box>

            {/* Always show tabs regardless of search state */}
            {!isIncludeOnly && (
            <Tabs
                value={activeTab}
                onChange={(_, newValue) => {
                    setActiveTab(newValue);
                    onModeSwitch?.(newValue === 0 ? "include" : "exclude");
                }}
                variant="fullWidth"
                sx={{
                    minHeight: "32px",
                    borderBottom: INCLUDE_EXCLUDE_BORDER,
                    "& .MuiTab-root": {
                        minHeight: "32px",
                        padding: 0,
                        fontSize: "10px",
                    },
                }}
            >
                <Tab
                    label={
                        <Box>
                            <AddCircleOutlineOutlined
                                sx={{
                                    color: theme.palette.success.main,
                                    fontSize: "12px",
                                    mr: 0.5,
                                }}
                            />{" "}
                            INCLUDE
                        </Box>
                    }
                    sx={{padding: "4px"}}
                />
                <Tab
                    label={
                        <Box>
                            <RemoveCircleOutlineOutlined
                                sx={{
                                    color: theme.palette.error.light,
                                    fontSize: "12px",
                                    mr: 0.5,
                                }}
                            />{" "}
                            EXCLUDE
                        </Box>
                    }
                    sx={{padding: "4px"}}
                />
            </Tabs>
            )}
            {/* List + overlay container */}
            <Box position="relative" flex={1} minHeight={0}>
                {/* Only show options list if not serverSide or (serverSide and searchTerm meets serverSearchMinChars) */}
                {(!isServerSide || (!serverError && searchTerm.length >= serverSearchMinChars)) && (
                    <Box
                        key={selectedCategory}
                        ref={listScrollRef}
                        data-testid="filter-list-scroll-container"
                        height="100%"
                        overflow="auto"
                        position="relative"
                    >
                        <Box
                            style={{
                                height: `${totalSize}px`,
                                position: "relative",
                                width: "100%",
                            }}
                        >
                            {virtualItems.map((virtualItem) => (
                                <Box
                                    key={virtualItem.key}
                                    ref={virtualizer.measureElement}
                                    data-index={virtualItem.index}
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        transform: `translateY(${virtualItem.start}px)`,
                                    }}
                                >
                                    <Row
                                        index={virtualItem.index}
                                        style={{minHeight: `${virtualItem.index === 0 && hasSelectAll ? selectAllSize : itemSize}px`}}
                                        flattenedOptionsWithSelectAll={flattenedOptionsWithSelectAll}
                                        selectedCategoryData={selectedCategoryData}
                                        getType={getType}
                                        selectedCategory={selectedCategory}
                                        dispatch={dispatch}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
                {isServerSide && serverSearchLoading && searchTerm.length >= serverSearchMinChars && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{bgcolor: "rgba(255,255,255,0.6)", zIndex: 1}}
                        role="status"
                        aria-live="polite"
                        aria-label="Loading results"
                    >
                        <CircularProgress size={32}/>
                    </Box>
                )}
                {isServerSide && searchTerm.length < serverSearchMinChars && (
                    <Alert severity="info" sx={{m: 2}}>
                        Please start by searching for a{" "}
                        {selectedCategoryData?.label ?? selectedCategory} to use as a
                        filter.
                    </Alert>
                )}
            </Box>

            {isServerSide && serverError && (
                <Alert severity="error" sx={{m: 2}}>
                    {serverError}
                </Alert>
            )}
        </Box>
    );
};
